import * as THREE from "three";
import { raysMesh } from "./chestLook";
import type { ChestCamera } from "./chestCamera";

/**
 * The radiant burst behind the stage's chest (chestLook.ts raysMesh) and its level: a faint breathing glow while the
 * chest waits that ramps with the player's taps, held at the burst's flash for its length, then a steady radiance by
 * rank. Always set back from the chest along the view and facing the camera. Presentation only.
 */
export class StageRays {
  readonly mesh: THREE.Mesh;
  private level = 0;
  private flash = 0;
  private sway = 0;
  /** How far behind the chest (world units, along the view). */
  static readonly Back = 1.6;

  constructor(colour: readonly number[]) { this.mesh = raysMesh(colour); }

  private get uniforms(): { uLevel: { value: number }; uColor: { value: THREE.Color } } {
    return (this.mesh.material as THREE.ShaderMaterial).uniforms as never;
  }

  colour(c: readonly number[]): void { this.uniforms.uColor.value.setRGB(c[0], c[1], c[2]); }

  /** Behind the chest as framed now, scaled with the camera's distance. */
  place(view: ChestCamera): void {
    this.mesh.position.copy(view.target).addScaledVector(_dir.copy(view.eye).sub(view.target).normalize(), -StageRays.Back);
    this.mesh.scale.setScalar(Math.max(1, view.distance / 4.2));
  }

  /** The burst: held at `level` for `seconds`. */
  burst(level: number, seconds: number): void { this.flash = seconds; this.level = level; }

  /** `want`: the level it eases toward (the stage's phase decides it). */
  update(dt: number, want: number, camera: THREE.Camera): void {
    this.sway += dt;
    if (this.flash > 0) this.flash -= dt;
    else this.level += (want - this.level) * Math.min(1, dt * 4);
    this.uniforms.uLevel.value = this.level;
    this.mesh.lookAt(camera.position);
  }

  /** The waiting glow: the charge's level, breathing. */
  waiting(charge: number): number { return charge * 0.7 + 0.02 * Math.sin(this.sway * 3); }
}

const _dir = new THREE.Vector3();
